import PropTypes from "prop-types";
import React, {useReducer, useState} from "react";
import OffersChoiceResultService from "services/OffersChoiceResultService";
import {makeStyles, TableCell, TableRow} from "@material-ui/core";
import Tr from "translations/Translations";
import moment from "libs/moment";
import DateFormats from "constants/DateFormats";
import WorkableIconButton from "components/button/WorkableIconButton";
import {KeyboardArrowDown as ArrowDownIcon, KeyboardArrowUp as ArrowUpIcon} from "@material-ui/icons";
import OfferChoiceResultFiles from "./OfferChoiceResultFiles";
import clsx from "clsx";
import {OfferChoiceResultStateLabel} from "../constants/OfferChoiceResultState";
import WorkableIconPrintButton from "components/button/WorkableIconPrintButton";
import OfferChoiceResultFilePublishPopup from "./OfferChoiceResultFilePublishPopup";
import WorkableIconCalendarButton from "components/button/WorkableIconCalendarButton";
import iconWorksReducer from "libs/iconWorksReducer";
import WorkableIconShareButton from "components/button/WorkableIconShareButton";
import ShareSelectPopup, {sharePopupData} from "components/popup/ShareSelectPopup";
import useSnackbar from "hooks/useSnackbar";

const useStyles = makeStyles(() => ({
    openFilesCell: {
        borderBottom: "none"
    },
    buttonCell: {
        padding: 0
    }
}));

function OfferChoiceResultRow(
    {
        demandId,
        editable,
        working,
        offerChoiceResultItem,
        showShare,
        canDownloadPaper,
        canDownloadFile
    }) {

    const classes = useStyles();
    const [publishPopupOpened, setPublishPopupOpened] = useState(false);

    const [iconWorks, dispatchIconWorks] = useReducer(iconWorksReducer, {
        anyWorks: false,
        load: false,
        print: false,
        publish: false,
        share: false
    });
    const [shareFilePopup, setShareFilePopup] = useState(null);
    const {enqueueSuccessSnackbar} = useSnackbar();
    const [files, setFiles] = useState(null);

    const onPrintClick = () => {
        if (!iconWorks.anyWorks) {
            dispatchIconWorks({type: 'print', works: true})
            OffersChoiceResultService.getChoiceResultPrint(demandId, offerChoiceResultItem.identity).finally(() => dispatchIconWorks({
                type: 'print',
                works: false
            }));
        }
    }

    const onUpdatePublishUntilDate = (values) => {
        dispatchIconWorks({type: 'publish', works: true})
        setPublishPopupOpened(false);
        OffersChoiceResultService.updatePublishUntilDate(demandId, offerChoiceResultItem.identity, values)
            .finally(() => dispatchIconWorks({type: 'publish', works: false}));
    }

    const onLoadButtonClick = () => {
        if (!iconWorks.anyWorks) {
            if (files === null) {
                dispatchIconWorks({type: 'load', works: true})
                OffersChoiceResultService.getChoiceResultFilesList(demandId, offerChoiceResultItem.identity).then((resp) => {
                    if (resp !== undefined)
                        setFiles(resp.files);
                }).finally(() => dispatchIconWorks({type: 'load', works: false}));
            } else {
                setFiles(null);
            }
        }
    }
    const name = `${Tr(offerChoiceResultItem.modification ? 'offers_choice_modification_result_applies_parts' : 'offers_choice_result_applies_parts')}: ${offerChoiceResultItem.positionOrders.join(", ")}`;

    const onShareFile = () => setShareFilePopup(sharePopupData(name));

    const doShare = (row, system) => {
        setShareFilePopup(null);
        dispatchIconWorks({type: 'share', works: true});
        OffersChoiceResultService.shareChoiceResultPrint(demandId, offerChoiceResultItem.identity, system).then((resp) => {
            if (resp)
                enqueueSuccessSnackbar(Tr("int_upload_success_x", {x: system}, true))
        }).finally(() => {
            dispatchIconWorks({type: 'share', works: false})
        });
    }

    const loadLastShareHistory = () => {
        return OffersChoiceResultService.loadLastShareHistory(demandId, offerChoiceResultItem.identity);
    }

    const loadShareHistory = (system) => {
        return OffersChoiceResultService.loadShareHistory(demandId, offerChoiceResultItem.identity, system);
    }

    const filesLoaded = files !== null;
    return (<>
        <TableRow>
            <TableCell className={clsx({[classes.openFilesCell]: filesLoaded})}>
                {name}
            </TableCell>
            <TableCell className={clsx({[classes.openFilesCell]: filesLoaded})}>
                {OfferChoiceResultStateLabel(offerChoiceResultItem.state)}
            </TableCell>
            <TableCell className={clsx({[classes.openFilesCell]: filesLoaded})}>
                {offerChoiceResultItem.publishDate ? moment(offerChoiceResultItem.publishDate).format(DateFormats.DATETIME_FORMAT) : null}
            </TableCell>
            <TableCell className={clsx({[classes.openFilesCell]: filesLoaded})}>
                {offerChoiceResultItem.publishUntilDate ? moment(offerChoiceResultItem.publishUntilDate).format(DateFormats.DATETIME_FORMAT) : null}
            </TableCell>
            <TableCell className={clsx(classes.buttonCell, {[classes.openFilesCell]: filesLoaded})}>
                <WorkableIconCalendarButton
                    //tooltip={Tr('generate_offer_choice_result_file', {parts: offerChoiceResultItem.positionOrders.join(", ")})}
                    working={iconWorks.publish}
                    disabled={!editable || iconWorks.anyWorks}
                    hideTooltip={iconWorks.publish}
                    onClick={() => setPublishPopupOpened(true)}
                />
            </TableCell>
            <TableCell className={clsx(classes.buttonCell, {[classes.openFilesCell]: filesLoaded})}>
                <WorkableIconPrintButton
                    tooltip={Tr('generate_offer_choice_result_file', {parts: offerChoiceResultItem.positionOrders.join(", ")})}
                    working={iconWorks.print}
                    disabled={!canDownloadPaper || iconWorks.anyWorks}
                    hideTooltip={iconWorks.print}
                    onClick={onPrintClick}
                />
            </TableCell>
            {
                showShare && <TableCell className={clsx(classes.buttonCell, {[classes.openFilesCell]: filesLoaded})}>
                    <WorkableIconShareButton
                        working={iconWorks.share}
                        disabled={iconWorks.anyWorks}
                        hideTooltip={iconWorks.share}
                        onClick={onShareFile}
                    />
                    {!!shareFilePopup && <ShareSelectPopup
                        data={shareFilePopup}
                        onCancel={() => setShareFilePopup(null)}
                        onChoose={doShare}
                        loadLastShareHistory={loadLastShareHistory}
                        loadShareHistory={(system) => loadShareHistory(system)}
                    />}

                </TableCell>
            }
            <TableCell className={clsx(classes.buttonCell, {[classes.openFilesCell]: filesLoaded})}>
                <WorkableIconButton
                    working={iconWorks.load}
                    disabled={iconWorks.anyWorks}
                    hideTooltip={iconWorks.load}
                    Icon={filesLoaded ? ArrowUpIcon : ArrowDownIcon}
                    onClick={onLoadButtonClick}
                    tooltip={filesLoaded ? Tr('collapse') : Tr('show_files')}
                />
            </TableCell>
        </TableRow>
        <OfferChoiceResultFiles
            demandId={demandId}
            working={working || iconWorks.anyWorks}
            editable={editable}
            offerChoiceResultItem={offerChoiceResultItem}
            files={files}
            setFiles={setFiles}
            showShare={showShare}
            canDownloadFile={canDownloadFile}
        />
        {
            publishPopupOpened &&
            <OfferChoiceResultFilePublishPopup
                offerChoiceResultItem={offerChoiceResultItem}
                onClose={() => setPublishPopupOpened(false)}
                onSubmit={onUpdatePublishUntilDate}
            />
        }
    </>)
}

OfferChoiceResultRow.propTypes = {
    demandId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    editable: PropTypes.bool.isRequired,
    working: PropTypes.bool,
    offerChoiceResultItem: PropTypes.shape({
        positionOrders: PropTypes.arrayOf(PropTypes.number).isRequired,
        publishDate: PropTypes.string,
        state: PropTypes.string,
    }).isRequired
}

export default OfferChoiceResultRow;

